// The hints of a newcomer's first games (design 14 §9.4): a short line on the table, the first time its moment comes,
// once per account. Each states a computed fact of the position, never advice. A pure function of the view; the
// layer that shows them is ui/Hint.

import { ActionType } from "../pb/mj/record/v1/record_pb";
import { PromptKind } from "../pb/mj/protocol/v1/protocol_pb";
import { t } from "../strings";
import { tileWords } from "./tiles";
import type { View } from "./view";

export const HINT_IDS = ["first_turn", "bank", "ready", "call", "riichi", "win", "result"] as const;
export type HintId = (typeof HINT_IDS)[number];

export interface Hint {
  id: HintId;
  text: string;
}

/** The tile on offer in a call window: the last discard. */
function offered(v: View): string {
  const d = v.lastDiscard;
  const tile = d ? v.seats[d.seat]?.river[d.index]?.tile : undefined;
  return tile === undefined ? "" : tileWords(tile, v.redFives);
}

/** A hand can be ready and still not win on these tiles: without a yaku, tenpai is only the shape. */
function readyText(a: NonNullable<View["aids"]>): string {
  const n = a.waits.length;
  if (a.noYaku.length === n) return t.hints.readyNoYaku;
  if (a.noYaku.length) return t.hints.readySomeNoYaku;
  if (a.noRon.length === n) return t.hints.readyNoRon;
  return a.noRon.length ? t.hints.readySomeNoYaku : t.hints.ready;
}

/** What to say now, the most pressing moment first; null when nothing new has come. `bank`: the seat's own move has
 *  used its time and its bank is running; `result`: the hand's result is on the screen; `touch`: the device is played with a finger. */
export function hintFor(v: View, seen: readonly string[], bank: boolean, result: boolean, touch = false): Hint | null {
  const fresh = (id: HintId) => !seen.includes(id);
  if (result) return fresh("result") ? { id: "result", text: t.hints.result } : null;
  const p = v.prompt;
  if (p && !p.sent) {
    const legal = p.prompt.legal;
    const has = (type: ActionType) => legal.some((a) => a.type === type);
    if (fresh("win") && has(ActionType.RON)) return { id: "win", text: t.hints.win("Ron") };
    if (fresh("win") && has(ActionType.TSUMO)) return { id: "win", text: t.hints.win("Tsumo") };
    if (fresh("riichi") && has(ActionType.DECLARE_RIICHI)) return { id: "riichi", text: t.hints.riichi };
    if (fresh("call") && p.prompt.kind === PromptKind.WINDOW) {
      if (has(ActionType.PON)) return { id: "call", text: t.hints.pon(offered(v)) };
      if (has(ActionType.CHI)) return { id: "call", text: t.hints.chi(offered(v)) };
      if (has(ActionType.OPEN_KAN)) return { id: "call", text: t.hints.kan(offered(v)) };
    }
  }
  if (fresh("ready") && (v.aids?.waits.length ?? 0) > 0) return { id: "ready", text: readyText(v.aids!) };
  if (p && !p.sent) {
    if (fresh("bank") && bank) return { id: "bank", text: t.hints.bank };
    if (fresh("first_turn") && p.prompt.kind === PromptKind.AFTER_DRAW && p.prompt.legal.some((a) => a.type === ActionType.DISCARD)) {
      return { id: "first_turn", text: touch ? t.hints.firstTurnTouch : t.hints.firstTurn };
    }
  }
  return null;
}
